import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../contexts/AuthContext';

const PRIORITY_COLORS = {
  Low: 'bg-green-100 text-green-700',
  Medium: 'bg-yellow-100 text-yellow-700',
  High: 'bg-orange-100 text-orange-700',
  Urgent: 'bg-red-100 text-red-700',
};

const GlobalSearch = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);

  const closePanel = () => {
    setPanelOpen(false);
    setOpen(false);
    setQuery('');
    setResults([]);
  };

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const performSearch = useCallback(async (term) => {
    if (!term || term.trim() === '') {
      setResults([]);
      setOpen(false);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`/api/search?q=${encodeURIComponent(term.trim())}`, {
        headers: { 'x-auth-token': token }
      });
      setResults(res.data);
      setOpen(true);
    } catch (err) {
      console.error('Search failed:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!val || val.trim() === '') {
      setResults([]);
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      performSearch(val);
    }, 300);
  };

  const handleResultClick = (card) => {
    closePanel();
    navigate(`/boards/${card.board._id}?cardId=${card._id}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      closePanel();
    }
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        closePanel();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus the input as soon as the panel opens.
  useEffect(() => {
    if (panelOpen) inputRef.current?.focus();
  }, [panelOpen]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div ref={containerRef} className="relative">
      {/* Search icon toggle */}
      <button
        onClick={() => (panelOpen ? closePanel() : setPanelOpen(true))}
        aria-label="Search cards"
        title="Search cards"
        className={`p-2 rounded-xl transition-all focus:outline-none hover:bg-gray-50 dark:hover:bg-gray-800 ${panelOpen ? 'text-primary-blue' : 'text-gray-400 hover:text-primary-blue'}`}
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
          />
        </svg>
      </button>

      {/* Search panel */}
      {panelOpen && (
        <div className="absolute right-0 top-full mt-3 w-[calc(100vw-2rem)] max-w-sm bg-white dark:bg-gray-800 rounded-2xl shadow-2xl ring-1 ring-black/5 dark:ring-white/10 z-50 animate-fade-in">
          <div className="relative flex items-center p-2">
            <svg
              className="absolute left-5 h-4 w-4 text-gray-400 pointer-events-none"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
              />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder="Search cards..."
              className="w-full pl-9 pr-8 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-blue/30 focus:border-primary-blue transition-all"
            />
            {loading && (
              <svg
                className="absolute right-5 h-4 w-4 animate-spin text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            )}
          </div>

          {/* Results */}
          {open && (results.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-gray-400 dark:text-gray-500 font-bold uppercase tracking-widest">
              No results found
            </div>
          ) : (
            <ul className="max-h-80 overflow-y-auto divide-y divide-gray-50 dark:divide-gray-700">
              {results.map((card) => (
                <li key={card._id}>
                  <button
                    onMouseDown={(e) => {
                      // Use mousedown to fire before onBlur hides the dropdown
                      e.preventDefault();
                      handleResultClick(card);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-start justify-between gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 dark:text-gray-100 truncate">{card.title}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 font-semibold mt-0.5 truncate">
                        {card.board?.name}
                      </p>
                    </div>
                    {card.priority && (
                      <span
                        className={`flex-shrink-0 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${PRIORITY_COLORS[card.priority] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}
                      >
                        {card.priority}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          ))}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
